import { useQuery } from '@tanstack/react-query';
import Axios from 'axios';

import { logger } from '@zep/core-logger';

import { AXIOS_INSTANCE } from '@/api/httpClient';
import { coreBaseUrl } from '@/consts/env';
import { useUser } from '@/lib/auth';

/**
 * 코어 아바타 카탈로그 응답.
 * 코어 `@zep/apis`의 `AvatarCatalog` 와 동일 구조 (service=quiz 카탈로그).
 */
export interface AvatarShapePart {
  hex: string | null;
  partId: number;
  imageVersion: number;
}

export interface AvatarShape {
  id: number;
  name: string;
  parts: AvatarShapePart[];
  previewPartId: number;
  isNew: boolean;
  isHot: boolean;
  acquisitionMode?: string;
  price?: number;
  currency?: string;
  isFree?: boolean;
  owned?: boolean;
  usable?: boolean;
}

export interface AvatarCatalogCategory {
  id: number;
  name: string;
  slug: string;
  hasColor: boolean;
}

export interface AvatarCatalog {
  categories: AvatarCatalogCategory[];
  shapes: Record<string, AvatarShape[]>;
}

export type AvatarServiceKey = 'core' | 'quiz';

export const QUERY_KEY_AVATAR_CATALOG = 'avatar-catalog';

const AVATAR_CATALOG_INSTANCE = Axios.create({
  baseURL: `${coreBaseUrl}/api`,
  withCredentials: true,
});

interface QuizAvatarShape {
  shapeId: string;
  price: number;
  isFree: boolean;
  owned: boolean;
  usable: boolean;
}

interface QuizAvatarShapeList {
  items: QuizAvatarShape[];
  nextCursor?: string | null;
}

/**
 * 소유권 오버레이를 커서 끝까지 모아 온다.
 *
 * 상한 없는 `do...while` 이지만 서버가 종료를 보장한다 — 커서는 offset 기반이고
 * (`CoreAvatarCatalogClient.listQuizShapes`), `limit` 은 1~100 으로 강제되므로
 * (`validateLimit`) 매 페이지 offset 이 최소 1 씩 전진한다. 커서가 제자리에 머물 수 없다.
 * 서버가 opaque 커서로 바뀌면 이 전제가 깨지니 그때 상한을 넣을 것.
 */
async function fetchQuizShapeOverlay() {
  const shapes: QuizAvatarShape[] = [];
  let cursor: string | undefined;

  do {
    const { data } = await AXIOS_INSTANCE.get('/avatar/shapes', {
      params: { limit: 100, cursor },
    });
    const page = (data?.data ?? data) as QuizAvatarShapeList;
    shapes.push(...(page.items ?? []));
    cursor = page.nextCursor ?? undefined;
  } while (cursor);

  return new Map(shapes.map(shape => [Number(shape.shapeId), shape]));
}

function normalizeQuizCatalog(
  catalog: AvatarCatalog,
  userType?: string,
  quizOverlay?: Map<number, QuizAvatarShape> | null,
): AvatarCatalog {
  return {
    ...catalog,
    shapes: Object.fromEntries(
      Object.entries(catalog.shapes).map(([shapeKey, shapes]) => [
        shapeKey,
        shapes.map(shape => {
          const overlay = quizOverlay?.get(shape.id);
          const isFree =
            overlay?.isFree ??
            shape.isFree ??
            !(
              shape.acquisitionMode?.toLowerCase() === 'purchase' &&
              (shape.price ?? 0) > 0
            );
          const usable =
            overlay?.usable ??
            (userType?.toLowerCase() === 'student' || !userType
              ? isFree
              : true);

          return {
            ...shape,
            price: overlay?.price ?? shape.price,
            currency: overlay ? 'point' : shape.currency,
            isFree,
            owned: overlay?.owned ?? isFree,
            usable,
          };
        }),
      ]),
    ),
  };
}

export function useAvatarCatalog(
  service: AvatarServiceKey = 'quiz',
  options?: { enabled?: boolean },
) {
  const { user } = useUser();
  const shouldOverlayQuizOwnership = service === 'quiz' && Boolean(user);

  return useQuery<AvatarCatalog>({
    queryKey: [QUERY_KEY_AVATAR_CATALOG, service, user?.quizUserId],
    queryFn: async () => {
      const [{ data }, quizOverlay] = await Promise.all([
        AVATAR_CATALOG_INSTANCE.get('/avatar/catalog', {
          params: { service },
        }),
        shouldOverlayQuizOwnership
          ? // 소유권 조회 실패를 폴백 가능한 데이터로 취급하면 안 된다. null 로 넘기면
            // normalizeQuizCatalog 가 구매한 파츠까지 owned/usable=false 로 계산하고,
            // AvatarPartSelector 의 sanitizeUnownedParts 가 착용 중인 구매 파츠를
            // 무료 파츠로 갈아끼운다. 그 상태로 다른 항목을 저장하면 구매 파츠가
            // 실제 프로필에서 사라진다. 에러를 그대로 올려 편집/저장 자체를 막는다
            // (sanitize 는 catalog 가 있을 때만 도므로 쿼리가 실패하면 실행되지 않는다).
            fetchQuizShapeOverlay().catch(error => {
              logger.debug('avatar_quiz_overlay_failed', {
                error: error instanceof Error ? error.message : String(error),
              });
              throw error;
            })
          : Promise.resolve(null),
      ]);
      // 코어 응답은 { data: {...} } 또는 평면 객체 둘 다 올 수 있어 호환 처리.
      const catalog = (data?.data ?? data) as AvatarCatalog;

      if (service !== 'quiz') return catalog;

      return normalizeQuizCatalog(catalog, user?.type, quizOverlay);
    },
    staleTime: 30 * 60 * 1000,
    ...options,
  });
}
